import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui-kit/button';

export interface PageHeaderAction {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant?: React.ComponentProps<typeof Button>['variant'];
  disabled?: boolean;
  loading?: boolean;
}

interface PageHeaderProps {
  title: React.ReactNode;
  actions?: PageHeaderAction[];
  /** Anything else on the right, after the actions (e.g. a view toggle). */
  children?: React.ReactNode;
  /** Anything on the right before the actions (e.g. a table's filter button on a phone). */
  beforeActions?: React.ReactNode;
  className?: string;
}

/**
 * A page's title with its actions on one line. On a phone (below 768px) an action shows only its icon
 * in a 40px square button, keeping its label for screen readers and as a tooltip, as the old portal's
 * mobile action buttons did.
 */
export const PageHeader = ({
  title,
  actions = [],
  children,
  beforeActions,
  className,
}: PageHeaderProps) => (
  <div className={cn('flex items-center justify-between gap-3', className)}>
    <h1 className="min-w-0 truncate text-lg font-semibold">{title}</h1>
    {(actions.length > 0 || children || beforeActions) && (
      <div className="flex shrink-0 items-center gap-2">
        {/* On a phone its buttons (e.g. a table's filter button) are as tall as the icon actions. */}
        <div className="contents max-md:[&_button]:size-10">{beforeActions}</div>
        {actions.map((action) => (
          <Button
            key={action.label}
            type="button"
            variant={action.variant}
            disabled={action.disabled}
            loading={action.loading}
            onClick={action.onClick}
            title={action.label}
            aria-label={action.label}
            className="max-md:h-10 max-md:w-10 max-md:p-0"
          >
            {!action.loading && action.icon}
            <span className="max-md:sr-only">{action.label}</span>
          </Button>
        ))}
        {children}
      </div>
    )}
  </div>
);
